import { useEffect, useRef, useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useContent, formatPrice } from "@/context/ContentContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, User as UserIcon, Smartphone } from "lucide-react";
import ShippingEstimator from "@/components/ShippingEstimator";

// Lazily load Razorpay checkout.js
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { content } = useContent();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [shipping, setShipping] = useState({
    name: user?.name || "", phone: "", address_line1: "", address_line2: "",
    landmark: "", city: "", state: "", postal_code: "", country: "India",
  });
  const [guestEmail, setGuestEmail] = useState("");
  const [pincodeStatus, setPincodeStatus] = useState({ state: "idle", info: null });
  const pinTimer = useRef(null);

  const [methods, setMethods] = useState({ razorpay: { enabled: true } });
  const [gateway] = useState("razorpay");
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [rateChecked, setRateChecked] = useState(false);  // customer must click "Check Shipping Rates" AND pick a rate
  const [bulkRows, setBulkRows] = useState([]);  // [{product_id, bulk_qty, name, quantity}]

  // Fetch bulk_qty for cart items to show bulk-order notice
  useEffect(() => {
    if (items.length === 0) { setBulkRows([]); return; }
    const ids = items.map((i) => i.product_id);
    api.post("/products/dims", { product_ids: ids }).then(({ data }) => {
      const map = {};
      data.forEach((d) => { map[d.product_id] = d.bulk_qty || 0; });
      const flagged = items
        .filter((it) => (map[it.product_id] || 0) > 0 && it.quantity >= map[it.product_id])
        .map((it) => ({ product_id: it.product_id, name: it.name, quantity: it.quantity, bulk_qty: map[it.product_id] }));
      setBulkRows(flagged);
    }).catch(() => setBulkRows([]));
  }, [items]);

  const shippingCharge = selectedShipping?.rate || 0;
  const grandTotal = subtotal + shippingCharge;

  useEffect(() => {
    api.get("/payments/methods").then(({ data }) => {
      setMethods(data);
    }).catch(() => {});
  }, []);

  const setF = (k) => (e) => setShipping((s) => ({ ...s, [k]: e.target.value }));

  // Guest email storage so PaymentSuccess can prefill it for account creation
  useEffect(() => {
    if (!user) {
      const stored = localStorage.getItem("tmf_guest_email");
      if (stored) setGuestEmail(stored);
    }
  }, [user]);

  // Debounced pincode check
  const onPincodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setShipping((s) => ({ ...s, postal_code: val }));
    if (pinTimer.current) clearTimeout(pinTimer.current);
    if (val.length !== 6) {
      setPincodeStatus({ state: "idle", info: null });
      return;
    }
    setPincodeStatus({ state: "checking", info: null });
    pinTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/pincodes/check/${val}`);
        setPincodeStatus({ state: data.available ? "ok" : "no", info: data });
      } catch {
        setPincodeStatus({ state: "no", info: { reason: "Unable to verify pincode" } });
      }
    }, 350);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Cart is empty"); return; }
    if (!user && !guestEmail.trim()) { toast.error("Enter your email to continue"); return; }
    if (pincodeStatus.state !== "ok") {
      toast.error("Please enter a serviceable 6-digit pincode to proceed to payment.");
      return;
    }
    setBusy(true);
    try {
      const orderRes = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping,
        email: user ? undefined : guestEmail.trim(),
        name: user ? undefined : shipping.name,
        shipping_charge: shippingCharge,
        shipping_method: selectedShipping ? {
          courier_name: selectedShipping.courier_name,
          rate: selectedShipping.rate,
          eta_days: selectedShipping.eta_days,
          etd: selectedShipping.etd,
          id: selectedShipping.id,
          is_cargo: !!selectedShipping.is_cargo,
        } : null,
      });
      const order = orderRes.data;
      // Persist guest email so success page can prefill the account form
      if (!user) {
        localStorage.setItem("tmf_guest_email", guestEmail.trim());
        localStorage.setItem("tmf_last_order_id", order.order_id);
      }

      if (methods.razorpay?.enabled) {
        await payWithRazorpay(order);
      } else {
        toast.error("Payments are temporarily disabled. Please contact support or use WhatsApp to place your order.");
        setBusy(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed");
      setBusy(false);
    }
  };

  const payWithRazorpay = async (order) => {
    const ok = await loadRazorpayScript();
    if (!ok) {
      toast.error("Unable to load Razorpay. Check your internet connection.");
      setBusy(false);
      return;
    }
    let init;
    try {
      const { data } = await api.post(`/payments/razorpay/create-order/${order.order_id}`, {
        origin_url: window.location.origin,
      });
      init = data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Razorpay init failed");
      setBusy(false);
      return;
    }

    const rzp = new window.Razorpay({
      key: init.key_id,
      amount: init.amount,
      currency: init.currency,
      order_id: init.razorpay_order_id,
      name: content?.brand?.name || "TMF by Silver Sure",
      description: `Order #${order.order_id}`,
      image: content?.brand?.logo_url || undefined,
      prefill: {
        name: init.customer?.name || "",
        email: init.customer?.email || "",
        contact: init.customer?.contact || "",
      },
      theme: { color: "#0A66C2" },
      notes: { tmf_order_id: order.order_id },
      handler: async (resp) => {
        try {
          await api.post("/payments/razorpay/verify", {
            order_id: order.order_id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          clear();
          window.location.href = `/payment/success?order_id=${order.order_id}&gateway=razorpay`;
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
          setBusy(false);
        }
      },
      modal: {
        ondismiss: () => {
          toast.info("Payment cancelled. Your order is saved as unpaid.");
          setBusy(false);
        },
      },
    });
    rzp.on("payment.failed", (resp) => {
      toast.error(resp.error?.description || "Payment failed. Please retry.");
      setBusy(false);
    });
    rzp.open();
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display font-black text-3xl uppercase tracking-tight mb-4 text-tmf-navy">Your cart is empty</h1>
        <Button onClick={() => nav("/products")} className="rounded-none bg-tmf-blue hover:bg-tmf-navy uppercase tracking-widest">Browse Products</Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 animate-tmf-fade">
      <div className="border-b border-slate-200 pb-6 mb-8">
        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">Step 2 of 3</div>
        <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight text-tmf-navy">Checkout</h1>
        {!user && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5 bg-blue-50 text-tmf-blue border border-blue-100 px-2 py-1 font-mono-tmf uppercase tracking-widest text-[10px] font-bold">
              <UserIcon size={12} /> Guest Checkout
            </span>
            <span>Skip signup for now — you can create an account after payment to save this order.</span>
            <Link to="/login?redirect=/checkout" data-testid="checkout-login-link" className="text-tmf-blue font-bold underline">Already have an account?</Link>
          </div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-8">
        <form onSubmit={submit} className="col-span-12 lg:col-span-8 space-y-8">
          <div className="border border-slate-200 p-6 md:p-8">
            <div className="font-display font-bold uppercase tracking-widest text-sm mb-6 pb-3 border-b border-slate-200">
              {user ? "Shipping Address" : "Your Contact & Shipping"}
            </div>

            {!user && (
              <div className="mb-4">
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Email (for order confirmation)</label>
                <Input data-testid="guest-email" required type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} className="rounded-none mt-1 border-slate-300" placeholder="you@company.com" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Full Name</label>
                <Input data-testid="ship-name" required value={shipping.name} onChange={setF("name")} className="rounded-none mt-1 border-slate-300" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Phone</label>
                <Input data-testid="ship-phone" required value={shipping.phone} onChange={setF("phone")} className="rounded-none mt-1 border-slate-300" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Country</label>
                <Input data-testid="ship-country" required value={shipping.country} onChange={setF("country")} className="rounded-none mt-1 border-slate-300" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Address Line 1</label>
                <Input data-testid="ship-line1" required value={shipping.address_line1} onChange={setF("address_line1")} className="rounded-none mt-1 border-slate-300" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Address Line 2 (optional)</label>
                <Input value={shipping.address_line2} onChange={setF("address_line2")} className="rounded-none mt-1 border-slate-300" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Landmark (optional — helps couriers find you)</label>
                <Input data-testid="ship-landmark" value={shipping.landmark} onChange={setF("landmark")} placeholder="e.g. Opposite City Hospital" className="rounded-none mt-1 border-slate-300" />
              </div>
              <div>
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">City</label>
                <Input data-testid="ship-city" required value={shipping.city} onChange={setF("city")} className="rounded-none mt-1 border-slate-300" />
              </div>
              <div>
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">State</label>
                <Input data-testid="ship-state" required value={shipping.state} onChange={setF("state")} className="rounded-none mt-1 border-slate-300" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Pincode</label>
                <div className="relative mt-1">
                  <Input
                    data-testid="ship-postal"
                    required inputMode="numeric" maxLength={6}
                    value={shipping.postal_code}
                    onChange={onPincodeChange}
                    className={`rounded-none border-slate-300 pr-10 ${pincodeStatus.state === "ok" ? "border-green-500" : pincodeStatus.state === "no" ? "border-red-500" : ""}`}
                    placeholder="6-digit pincode"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    {pincodeStatus.state === "checking" && <Loader2 size={16} className="animate-spin text-slate-400" />}
                    {pincodeStatus.state === "ok" && <CheckCircle2 size={18} className="text-green-600" />}
                    {pincodeStatus.state === "no" && <XCircle size={18} className="text-red-600" />}
                  </div>
                </div>
                {pincodeStatus.state === "ok" && (
                  <div data-testid="pin-ok" className="mt-1.5 text-xs font-mono-tmf uppercase tracking-widest text-green-700 flex items-center gap-1.5">
                    <CheckCircle2 size={12} /> Delivery available{pincodeStatus.info?.eta_days ? ` · ETA ${pincodeStatus.info.eta_days} days` : ""}
                  </div>
                )}
                {pincodeStatus.state === "no" && (
                  <div data-testid="pin-no" className="mt-1.5 text-xs font-mono-tmf uppercase tracking-widest text-red-600 flex items-center gap-1.5">
                    <XCircle size={12} /> {pincodeStatus.info?.reason || "Not serviceable to this pincode"}
                  </div>
                )}

                {pincodeStatus.state === "ok" && items.length > 0 && (
                  <div className="mt-4">
                    <ShippingEstimator
                      items={items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }))}
                      initialPin={shipping.postal_code}
                      pinReadOnly
                      compact
                      selectable
                      onSelect={setSelectedShipping}
                      onCheckedChange={setRateChecked}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>

        <div className="col-span-12 lg:col-span-4">
          <div className="border border-tmf-navy bg-white sticky top-28">
            <div className="bg-tmf-navy text-white px-5 py-3 font-display font-bold uppercase tracking-widest text-xs">Order Summary</div>
            {bulkRows.length > 0 && (
              <div data-testid="checkout-bulk-banner" className="border-b border-amber-200 bg-amber-50 px-5 py-3 text-[11px] text-amber-900">
                <div className="flex items-center gap-2 font-bold uppercase tracking-widest text-[10px] mb-1">Bulk Order Detected</div>
                <div>Your order includes {bulkRows.length} bulk-quantity {bulkRows.length === 1 ? "line" : "lines"}. Please proceed with payment — <b>our sales team will contact you within 24 hours for a personalised bulk quote</b> (up to 25% off factory price).</div>
              </div>
            )}
            <div className="p-5 space-y-2 max-h-64 overflow-auto text-sm">
              {items.map((i) => (
                <div key={i.product_id} className="flex justify-between gap-2">
                  <span className="truncate">{i.name} × {i.quantity}</span>
                  <span className="font-mono-tmf whitespace-nowrap">{formatPrice(i.price * i.quantity, content)}</span>
                </div>
              ))}
            </div>
            <div className="p-5 space-y-2 border-t border-slate-200 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-mono-tmf font-bold">{formatPrice(subtotal, content)}</span></div>
              <div className="flex justify-between">
                <span className="text-slate-600">
                  Shipping{selectedShipping ? ` · ${selectedShipping.courier_name}` : ""}
                </span>
                <span className="font-mono-tmf font-bold" data-testid="shipping-line">
                  {selectedShipping ? formatPrice(shippingCharge, content) : <span className="text-slate-400 font-normal">Choose above</span>}
                </span>
              </div>
              <div className="flex justify-between font-display font-black text-lg pt-2 border-t border-slate-200 text-tmf-navy">
                <span>Total</span>
                <span data-testid="grand-total">{formatPrice(grandTotal, content)}</span>
              </div>
            </div>

            {methods.razorpay?.enabled ? (
              <div className="px-5 pt-4 pb-2 border-t border-slate-200">
                <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500 mb-3">Payment Method</div>
                <div className="flex items-center gap-3 border border-tmf-blue bg-blue-50 p-3">
                  <Smartphone size={18} className="text-tmf-blue" />
                  <div className="flex-1">
                    <div data-testid="pm-razorpay" className="font-bold text-xs uppercase tracking-widest text-tmf-navy">Razorpay</div>
                    <div className="text-[11px] text-slate-500">UPI · Cards · Netbanking · Wallets</div>
                  </div>
                  <span className="text-[9px] font-mono-tmf uppercase tracking-widest bg-green-100 text-green-800 px-1.5 py-0.5">India</span>
                </div>
              </div>
            ) : (
              <div className="px-5 pt-4 pb-2 border-t border-slate-200">
                <div className="text-xs text-amber-700 font-mono-tmf uppercase tracking-widest bg-amber-50 border border-amber-200 p-2">
                  Payment gateway is being configured. Please try again shortly or contact us via WhatsApp.
                </div>
              </div>
            )}

            {pincodeStatus.state !== "ok" && (
              <div className="px-5 pt-4 pb-1 text-[11px] text-amber-700 font-mono-tmf uppercase tracking-widest flex items-center gap-2 border-t border-slate-200">
                <XCircle size={14} className="shrink-0" />
                {pincodeStatus.state === "no"
                  ? "Not serviceable — payment disabled"
                  : "Enter a serviceable 6-digit pincode to enable payment"}
              </div>
            )}
            {pincodeStatus.state === "ok" && !rateChecked && (
              <div data-testid="pay-gate-notice" className="px-5 pt-4 pb-1 text-[11px] text-amber-700 font-mono-tmf uppercase tracking-widest flex items-center gap-2 border-t border-slate-200">
                <XCircle size={14} className="shrink-0" />
                Please check shipping rates first for transparency
              </div>
            )}
            <Button
              data-testid="pay-now-btn"
              type="submit"
              onClick={submit}
              disabled={busy || pincodeStatus.state !== "ok" || !rateChecked}
              className="rounded-none w-full bg-tmf-blue hover:bg-tmf-navy h-12 uppercase tracking-widest text-sm font-bold hover-lift disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy
                ? "Redirecting…"
                : pincodeStatus.state !== "ok"
                  ? "Check Pincode First"
                  : !rateChecked
                    ? "Check Shipping Rates First"
                    : "Pay with Razorpay"}
            </Button>
            <div className="text-[10px] text-center py-3 font-mono-tmf uppercase tracking-widest text-slate-500 border-t border-slate-200">
              Razorpay test card: 4111 1111 1111 1111
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
